// src/components/Testimonials.tsx
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { TestimonialsData, TestimonialItem } from '../types/siteDataTypes';

interface TestimonialsProps {
  data: TestimonialsData;
  title?: string;
}

/* ─── Wrapper ────────────────────────────────────────────────────────────────── */

const Wrapper = styled.div`
  background: linear-gradient(145deg, rgba(239, 244, 249, 0.6), rgba(223, 231, 240, 0.6));
  border: 1px solid #cfd9e4;
  border-radius: 18px;
  padding: 28px 24px 32px;
  box-shadow: 0 20px 38px rgba(34, 50, 70, 0.08);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const Heading = styled.h3`
  color: #1f2b3a;
  margin: 0;
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
`;

const NavButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 1px solid rgba(31, 43, 58, 0.15);
  background: rgba(31, 43, 58, 0.05);
  color: #1f2b3a;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  transition: transform 0.2s ease, background 0.2s ease, border-color 0.2s ease, color 0.2s ease, opacity 0.2s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    background: rgba(129, 127, 253, 0.1);
    border-color: rgba(129, 127, 253, 0.4);
    color: #817ffd;
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
`;

/* ─── Scroll row ─────────────────────────────────────────────────────────────── */

const ScrollRow = styled.div`
  display: flex;
  gap: 14px;
  overflow-x: auto;
  scroll-behavior: smooth;
  scroll-snap-type: x proximity;
  scrollbar-width: none;
  -ms-overflow-style: none;
  padding-bottom: 4px;

  &::-webkit-scrollbar {
    display: none;
  }
`;

/* ─── Card ───────────────────────────────────────────────────────────────────── */

const Card = styled.div`
  flex: 0 0 240px;
  background: rgba(255, 255, 255, 0.65);
  border: 1px solid rgba(31, 43, 58, 0.1);
  border-radius: 14px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  scroll-snap-align: start;
  transition: background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.85);
    border-color: rgba(129, 127, 253, 0.25);
    box-shadow: 0 4px 16px rgba(129, 127, 253, 0.1);
  }
`;

const CardTop = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Avatar = styled.img`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid rgba(129, 127, 253, 0.5);
  flex-shrink: 0;
  background: #dde6ef;
`;

const Meta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const PersonName = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #1f2b3a;
  line-height: 1.2;
`;

const Quote = styled.p`
  font-size: 13px;
  color: #4a5a6b;
  line-height: 1.65;
  margin: 0;
  font-style: italic;
`;

/* ─── Component ──────────────────────────────────────────────────────────────── */

const Testimonials: React.FC<TestimonialsProps> = ({
  data,
  title = 'What people are saying',
}) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const syncButtons = () => {
    const el = scrollRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    const hasOverflow = max > 4;
    setCanLeft(hasOverflow && el.scrollLeft > 4);
    setCanRight(hasOverflow && el.scrollLeft < max - 4);
  };

  useEffect(() => {
    syncButtons();
    window.addEventListener('resize', syncButtons);
    return () => window.removeEventListener('resize', syncButtons);
  }, []);

  const scroll = (dir: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({
      left: dir === 'left' ? -Math.max(el.clientWidth * 0.8, 260) : Math.max(el.clientWidth * 0.8, 260),
      behavior: 'smooth',
    });
  };

  return (
    <Wrapper>
      <Header>
        <Heading>{title}</Heading>
        <Controls>
          <NavButton
            type="button"
            aria-label="Scroll testimonials left"
            onClick={() => scroll('left')}
            disabled={!canLeft}
          >
            ‹
          </NavButton>
          <NavButton
            type="button"
            aria-label="Scroll testimonials right"
            onClick={() => scroll('right')}
            disabled={!canRight}
          >
            ›
          </NavButton>
        </Controls>
      </Header>
      <ScrollRow ref={scrollRef} onScroll={syncButtons}>
        {data.map((t: TestimonialItem, i: number) => (
          <Card key={`${t.name}-${i}`}>
            <CardTop>
              <Avatar src={t.img} alt={t.name} />
              <Meta>
                <PersonName>{t.name}</PersonName>
              </Meta>
            </CardTop>
            <Quote>"{t.text}"</Quote>
          </Card>
        ))}
      </ScrollRow>
    </Wrapper>
  );
};

export default Testimonials;
